import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, Typography, Card, CardContent, Grid, Button, Table, TableHead, TableBody, TableRow,
  TableCell, TableContainer, Chip, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert
} from '@mui/material';
import { CheckCircle, Cancel, HourglassEmpty, Email } from '@mui/icons-material';
import { decisionsAPI, applicationsAPI, emailTemplatesAPI } from '../../services/api';
import { useSnackbar } from '../../utils/notify';
import dayjs from 'dayjs';

export default function DecisionManager() {
  const { id: formId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [decisions, setDecisions] = useState([]);
  const [apps, setApps] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState([]);
  const [decisionType, setDecisionType] = useState('accepted');
  const [notes, setNotes] = useState('');
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');
  const [emailCustomMsg, setEmailCustomMsg] = useState('');
  const [emailDecisions, setEmailDecisions] = useState([]);
  const [filter, setFilter] = useState('all');
  const [confirmFilter, setConfirmFilter] = useState('all');

  const load = () => {
    decisionsAPI.list(formId).then((r) => setDecisions(r.data.data));
    applicationsAPI.list(formId, { per_page: 200, status: 'reviewed' }).then((r) => setApps(r.data.data));
    emailTemplatesAPI.list().then((r) => setTemplates(r.data.data));
  };
  useEffect(load, [formId]);

  const handleMakeDecisions = async () => {
    if (selected.length === 0) return;
    try {
      await decisionsAPI.make({ application_ids: selected, decision: decisionType, notes });
      enqueueSnackbar(`${selected.length} decisions made`, { variant: 'success' });
      setSelected([]);
      load();
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
  };

  const handleSendEmails = async () => {
    try {
      const res = await decisionsAPI.sendEmails({
        decision_ids: emailDecisions,
        template_id: emailTemplate,
        custom_message: emailCustomMsg,
      });
      enqueueSnackbar(`${res.data.data.sent} emails sent`, { variant: 'success' });
      setEmailDialog(false);
      load();
    } catch (err) {
      enqueueSnackbar((err.response && err.response.data && err.response.data.error) || 'Failed', { variant: 'error' });
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const counts = {
    all: decisions.length,
    accepted: decisions.filter((d) => d.decision === 'accepted').length,
    rejected: decisions.filter((d) => d.decision === 'rejected').length,
    waitlisted: decisions.filter((d) => d.decision === 'waitlisted').length,
  };
  const byDecision = filter === 'all' ? decisions : decisions.filter((d) => d.decision === filter);
  const confirmCounts = {
    all: byDecision.length,
    confirmed: byDecision.filter((d) => d.scholar_accepted === true).length,
    pending: byDecision.filter((d) => d.decision === 'accepted' && !d.scholar_accepted).length,
  };
  let filtered = byDecision;
  if (confirmFilter === 'confirmed') filtered = byDecision.filter((d) => d.scholar_accepted === true);
  else if (confirmFilter === 'pending') filtered = byDecision.filter((d) => d.decision === 'accepted' && !d.scholar_accepted);
  const unnotified = filtered.filter((d) => !d.email_sent);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>Decisions</Typography>

      {/* Make decisions on reviewed apps */}
      {apps.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Pending Decisions ({apps.length} reviewed applications)
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Decision</InputLabel>
                <Select value={decisionType} label="Decision" onChange={(e) => setDecisionType(e.target.value)}>
                  <MenuItem value="accepted">Accept</MenuItem>
                  <MenuItem value="rejected">Reject</MenuItem>
                  <MenuItem value="waitlisted">Waitlist</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Button variant="contained" onClick={handleMakeDecisions} disabled={selected.length === 0}>
                Apply to {selected.length} selected
              </Button>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox checked={selected.length === apps.length && apps.length > 0}
                        indeterminate={selected.length > 0 && selected.length < apps.length}
                        onChange={() => setSelected(selected.length === apps.length ? [] : apps.map((a) => a.id))} />
                    </TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Avg Score</TableCell>
                    <TableCell>Reviews</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {apps.map((a) => (
                    <TableRow key={a.id} hover>
                      <TableCell padding="checkbox">
                        <Checkbox checked={selected.includes(a.id)} onChange={() => toggleSelect(a.id)} />
                      </TableCell>
                      <TableCell>{a.applicant_name}</TableCell>
                      <TableCell>{a.applicant_email}</TableCell>
                      <TableCell>{a.avg_score != null ? a.avg_score.toFixed(1) : '—'}</TableCell>
                      <TableCell>{a.review_count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* Existing decisions */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6">Decisions ({filtered.length})</Typography>
            {unnotified.length > 0 && (
              <Button variant="outlined" startIcon={<Email />} onClick={() => {
                setEmailDecisions(unnotified.map((d) => d.id));
                setEmailDialog(true);
              }}>
                Send {unnotified.length} unsent {filter === 'all' ? '' : filter + ' '}notification{unnotified.length === 1 ? '' : 's'}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            {['all', 'accepted', 'rejected', 'waitlisted'].map((key) => (
              <Chip key={key} clickable
                label={`${key.charAt(0).toUpperCase() + key.slice(1)} (${counts[key]})`}
                color={filter === key ? 'primary' : 'default'}
                variant={filter === key ? 'filled' : 'outlined'}
                onClick={() => setFilter(key)} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Scholar confirmation:</Typography>
            {[['all', 'All'], ['confirmed', 'Confirmed'], ['pending', 'Pending']].map((opt) => (
              <Chip key={opt[0]} clickable size="small"
                label={`${opt[1]} (${confirmCounts[opt[0]]})`}
                color={confirmFilter === opt[0] ? 'secondary' : 'default'}
                variant={confirmFilter === opt[0] ? 'filled' : 'outlined'}
                onClick={() => setConfirmFilter(opt[0])} />
            ))}
          </Box>
          <TableContainer>
            <Table size="small" sx={{ minWidth: 780 }}>
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 600, whiteSpace: 'nowrap' } }}>
                  <TableCell>Applicant</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Decision</TableCell>
                  <TableCell align="center">Avg Score</TableCell>
                  <TableCell>Email Sent</TableCell>
                  <TableCell>Scholar Confirmed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                      No {filter === 'all' ? '' : filter + ' '}decisions yet.
                    </Typography>
                  </TableCell></TableRow>
                )}
                {filtered.map((d) => (
                  <TableRow key={d.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.applicant_name}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-all', maxWidth: 220 }}>{d.applicant_email}</TableCell>
                    <TableCell>
                      <Chip label={d.decision} size="small" sx={{ textTransform: 'capitalize' }}
                        color={d.decision === 'accepted' ? 'success' : d.decision === 'rejected' ? 'error' : 'warning'}
                        icon={d.decision === 'accepted' ? <CheckCircle /> : d.decision === 'rejected' ? <Cancel /> : <HourglassEmpty />} />
                    </TableCell>
                    <TableCell align="center">{d.avg_score != null ? d.avg_score.toFixed(1) : '—'}</TableCell>
                    <TableCell>
                      {d.email_sent ? (
                        <Chip label={`Sent ${dayjs(d.email_sent_at).format('MMM D')}`} size="small" color="success" variant="outlined" />
                      ) : <Chip label="Not sent" size="small" variant="outlined" />}
                    </TableCell>
                    <TableCell>
                      {d.decision === 'accepted' ? (
                        d.scholar_accepted ? <Chip label="Confirmed" size="small" color="success" /> : <Chip label="Pending" size="small" />
                      ) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Email dialog */}
      <Dialog open={emailDialog} onClose={() => setEmailDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Send Decision Emails</DialogTitle>
        <DialogContent sx={{ pt: '16px !important' }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Sending to {emailDecisions.length} recipients
          </Alert>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Email Template</InputLabel>
            <Select value={emailTemplate} label="Email Template" onChange={(e) => setEmailTemplate(e.target.value)}>
              {templates.map((t) => <MenuItem key={t.id} value={t.id}>{t.name} ({t.template_type})</MenuItem>)}
            </Select>
          </FormControl>
          <TextField fullWidth label="Custom Message (replaces {{custom_message}})" multiline rows={3}
            value={emailCustomMsg} onChange={(e) => setEmailCustomMsg(e.target.value)}
            helperText="Supports HTML (e.g. <b>bold</b>, <a href='...'>links</a>). Emails are sent as HTML." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEmailDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSendEmails} disabled={!emailTemplate}>Send Emails</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
